import { inject, Pipe, PipeTransform } from '@angular/core';
import { PersonNounForm, PersonNounService } from '@settings/data-access';

/**
 * Breadcrumb labels whose rendered text follows the tenant's PersonTreatment
 * setting (Cliente / Aluno / Paciente / Membro) instead of the static i18n value.
 */
export const PERSON_NOUN_BREADCRUMB_LABELS: Record<string, PersonNounForm> = {
  'BREADCRUMB.CUSTOMERS.CUSTOMERS': 'plural',
  'BREADCRUMB.REPORTS.CUSTOMERS.CUSTOMERS': 'plural',
  'BREADCRUMB.REPORTS.CUSTOMERS.ACTIVE_CUSTOMERS': 'plural',
};

/**
 * Translates a breadcrumb/report label honoring the PersonTreatment setting:
 * labels in {@link PERSON_NOUN_BREADCRUMB_LABELS} render the noun itself;
 * every other label translates with the person-noun params available, so
 * values like `Novos {{people}}` interpolate the tenant's noun.
 *
 * An empty label is passed through — `TranslateService.instant` throws on one,
 * and optional labels (unset component inputs) legitimately arrive empty.
 */
export function translatePersonNounLabel(
  personNounService: PersonNounService,
  label: string
): string {
  if (!label) return label;
  const form = PERSON_NOUN_BREADCRUMB_LABELS[label];
  return form ? personNounService.noun(form) : personNounService.instant(label);
}

/**
 * Template counterpart of {@link translatePersonNounLabel}. Impure so it
 * re-resolves when the language or the PersonTreatment setting changes.
 */
@Pipe({
  name: 'personNounLabel',
  standalone: true,
  pure: false,
})
export class PersonNounLabelPipe implements PipeTransform {
  private readonly personNounService = inject(PersonNounService);

  public transform(label: string): string {
    return translatePersonNounLabel(this.personNounService, label);
  }
}
